import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import {
  Settings,
  Ticket,
  MessageSquareWarning,
  FileText,
  CircleHelp,
  Power,
  Code2,
} from "lucide-react";
import "../styles/profile-dropdown.css";

function avatarInitial(email?: string, name?: string): string {
  if (name && name.trim()) return name.trim().charAt(0).toUpperCase();
  if (email && email.trim()) return email.trim().charAt(0).toUpperCase();
  return "?";
}

const AVATAR_BG: Record<string, string> = {
  admin: "rgba(239, 68, 68, 0.2)",
  analyst: "rgba(168, 85, 247, 0.2)",
  client: "rgba(0, 102, 224, 0.2)",
};

const AVATAR_COLOR: Record<string, string> = {
  admin: "#ef4444",
  analyst: "#a855f7",
  client: "#818cf8",
};

interface ProfileDropdownProps {
  completionPct?: number;
}

export default function ProfileDropdown({ completionPct = 50 }: ProfileDropdownProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const role = user?.role ?? "client";
  const initial = avatarInitial(user?.email, user?.name);

  // SVG progress ring values
  const circleLength = 2 * Math.PI * 20; // r=20 for 44px box
  const strokeDashoffset = circleLength - (completionPct / 100) * circleLength;

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen]);

  // Close dropdown on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Navigation handlers
  const handleSettings = useCallback(() => {
    setIsOpen(false);
    navigate("/profile");
  }, [navigate]);

  const handleCoupon = useCallback(() => {
    setIsOpen(false);
    navigate("/coupon");
  }, [navigate]);

  const handleFeedback = useCallback(() => {
    setIsOpen(false);
    navigate("/feedback");
  }, [navigate]);

  const handleDocs = useCallback(() => {
    setIsOpen(false);
    window.open("https://threatactix.com/services/dark-web-monitoring/", "_blank", "noopener,noreferrer");
  }, []);

  const handleHelpCenter = useCallback(() => {
    setIsOpen(false);
    navigate("/help-center");
  }, [navigate]);

  const handleLogout = useCallback(() => {
    setIsOpen(false);
    logout();
  }, [logout]);

  // Keyboard navigation inside dropdown menu
  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const menuItems = dropdownRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
      if (!menuItems || menuItems.length === 0) return;
      
      const currentIndex = Array.from(menuItems).indexOf(document.activeElement as HTMLElement);
      let nextIndex = 0;
      if (e.key === "ArrowDown") {
        nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
      }
      menuItems[nextIndex]?.focus();
    }
  };

  // Availability flag for coupon feature
  const isCouponFeatureAvailable = true;

  return (
    <div className="tx-profile-dropdown-wrap" ref={dropdownRef}>
      {/* Avatar Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        className={`tx-avatar-trigger-btn ${isOpen ? "is-open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label="User account menu"
        title={user?.email || "Account Menu"}
      >
        {completionPct < 100 && completionPct > 0 && (
          <svg className="avatar-progress-svg" viewBox="0 0 44 44" aria-hidden="true">
            <circle className="avatar-progress-track" cx="22" cy="22" r="20" />
            <circle
              className="avatar-progress-fill"
              cx="22"
              cy="22"
              r="20"
              strokeDasharray={circleLength}
              strokeDashoffset={strokeDashoffset}
            />
          </svg>
        )}
        <div
          className="avatar-circle"
          style={{
            background: AVATAR_BG[role] ?? AVATAR_BG.client,
            color: AVATAR_COLOR[role] ?? AVATAR_COLOR.client,
          }}
        >
          {initial}
        </div>
      </button>

      {/* Profile Dropdown Menu */}
      {isOpen && (
        <div
          className="tx-profile-menu"
          role="menu"
          aria-label="Profile navigation options"
          onKeyDown={handleMenuKeyDown}
        >
          {/* Group 1: Account */}
          <div className="tx-menu-group" role="none">
            {/* Settings */}
            <button
              type="button"
              className="tx-profile-menu-item"
              role="menuitem"
              onClick={handleSettings}
            >
              <div className="tx-profile-menu-icon">
                <Settings size={18} />
                {completionPct < 100 && <span className="tx-settings-dot" title="Incomplete profile" />}
              </div>
              <span className="tx-menu-label">Settings</span>
            </button>

            {/* Coupon */}
            <button
              type="button"
              className="tx-profile-menu-item"
              role="menuitem"
              onClick={handleCoupon}
            >
              <div className="tx-profile-menu-icon">
                <Ticket size={18} />
              </div>
              <span className="tx-menu-label">Coupon</span>
              {isCouponFeatureAvailable && (
                <span className="tx-coupon-new-badge">NEW</span>
              )}
            </button>

            {/* API & Keys */}
            <button
              type="button"
              className="tx-profile-menu-item"
              role="menuitem"
              onClick={() => {
                setIsOpen(false);
                navigate("/api-access");
              }}
            >
              <div className="tx-profile-menu-icon">
                <Code2 size={18} />
              </div>
              <span className="tx-menu-label">API & Keys</span>
            </button>
          </div>

          <div className="tx-profile-menu-divider" role="separator" />

          {/* Group 2: Support */}
          <div className="tx-menu-group" role="none">
            {/* Feedback */}
            <button
              type="button"
              className="tx-profile-menu-item"
              role="menuitem"
              onClick={handleFeedback}
            >
              <div className="tx-profile-menu-icon">
                <MessageSquareWarning size={18} />
              </div>
              <span className="tx-menu-label">Feedback</span>
            </button>

            {/* Docs */}
            <button
              type="button"
              className="tx-profile-menu-item"
              role="menuitem"
              onClick={handleDocs}
            >
              <div className="tx-profile-menu-icon">
                <FileText size={18} />
              </div>
              <span className="tx-menu-label">Docs</span>
            </button>

            {/* Help Center */}
            <button
              type="button"
              className="tx-profile-menu-item"
              role="menuitem"
              onClick={handleHelpCenter}
            >
              <div className="tx-profile-menu-icon">
                <CircleHelp size={18} />
              </div>
              <span className="tx-menu-label">Help Center</span>
            </button>
          </div>

          <div className="tx-profile-menu-divider" role="separator" />

          {/* Group 3: Session */}
          <div className="tx-menu-group" role="none">
            {/* Logout */}
            <button
              type="button"
              className="tx-profile-menu-item logout-item"
              role="menuitem"
              onClick={handleLogout}
            >
              <div className="tx-profile-menu-icon">
                <Power size={18} />
              </div>
              <span className="tx-menu-label">Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
